<template>
    <form class="round-trip" @submit.prevent="search">
        <div class="inputs">
            <div class="inputs__dropdown">
                <label>Passenger</label>
                <Passengers :errorMessage="v$.passengers.$errors.map((e) => e.$message.toString())[0]" :hasReturn="hasReturn" />
            </div>
            <div class="inputs__dropdown">
                <label>Outward Route</label>
                <VSelect
                    class="outward-route"
                    :class="{ 'outward-route-return': hasReturn }"
                    placeholder="Select route"
                    v-model="v$.outwardRoute.$model"
                    :options="routes"
                    :hasError="v$.outwardRoute.$error"
                    @select="setReturnOptions"
                    :clearable="false"
                />
            </div>
            <div v-if="hasReturn" class="inputs__dropdown return-container">
                <div @click="switchRoutes" class="switch-icon"></div>
                <label>Return Route</label>
                <VSelect
                    class="return-route"
                    placeholder="Return route"
                    :options="returnRoutes"
                    v-model="v$.returnRoute.$model"
                    :hasError="v$.returnRoute.$error"
                    @select="onReturnRouteSelect"
                    :clearable="false"
                />
            </div>
            <div class="inputs__dropdown">
                <label>Dates</label>
                <VBtn
                    @click="showDates = !showDates"
                    class="dropdown-btn"
                    color="ghost"
                    :class="{ hasError: v$.outwardDate.$error || v$.returnDate.$error }"
                    type="button"
                >
                    <span>
                        {{ datesPlaceholder }}
                    </span>
                    <SvgIcon class="icon" icon="carbon/chevron-down" :class="{ open: showDates }" />
                </VBtn>
                <div
                    v-if="showDates"
                    ref="dropdownDate"
                    :class="{ 'no-return': !props.hasReturn }"
                    class="date-pickers dates-dropdown"
                    :key="JSON.stringify(initialPage())"
                >
                    <div class="date-pickers-container">
                        <h3>Outward date:</h3>
                        <VDatePicker
                            isInline
                            class="date-picker"
                            v-model="v$.outwardDate.$model"
                            :min-date="new Date()"
                            :max-date="outwardDates?.endDate"
                            :disabledDates="outwardDates?.disabledDates"
                            :disable="!state.outwardRoute.length"
                            weekdaysFormat="W"
                            :initial-page="initialPage(true)"
                        />
                    </div>
                    <div v-if="hasReturn" class="date-pickers-container">
                        <h3>Return date:</h3>

                        <VDatePicker
                            isInline
                            class="date-picker"
                            v-model="v$.returnDate.$model"
                            :min-date="v$.outwardDate.$model ?? new Date()"
                            :max-date="returnDates?.endDate"
                            :disabledDates="returnDates?.disabledDates"
                            :disable="!state.returnRoute.length"
                            weekdaysFormat="W"
                            :initial-page="initialPage()"
                        />
                    </div>
                </div>
            </div>
            <div class="inputs__dropdown">
                <label>Vehicle</label>
                <VehicleForm :hasReturn="hasReturn" />
            </div>
        </div>
        <div class="button-submit">
            <VBtn :isLoading="isLoading">Book now</VBtn>
        </div>
    </form>
</template>

<script setup lang="ts">
import SvgIcon from "@/components/base/SvgIcon.vue";
import useDialog from "@/composables/useDialog";
import { dateObject } from "@/helpers/dateHelpers";
import {
    createRequest,
    getDatesPlaceholder,
    getDefaultReturn,
    getNextValidDate,
    getReturnRoutes,
    isValidPassengers,
} from "@/helpers/quickformHelpers";
import { Direction } from "@/models/Enums";
import { DialogType } from "@/models/symbols/Dialog";
import { Filter } from "@/modules/AvailabilityCalendar/models/AvailabilityFilter";
import { routes } from "@/modules/Quickform/config/routes";
import { usePassengerStore } from "@/store/passengerStore";
import { useQuickFormStore } from "@/store/quickFormStore";
import { useVehicleStore } from "@/store/vehicleStore";
import useVuelidate from "@vuelidate/core";
import { helpers, required, requiredIf } from "@vuelidate/validators";
import { onClickOutside } from "@vueuse/core";
import { AxiosError } from "axios";
import { VBtn, VDatePicker, VSelect } from "v-ferry-components";
import { computed, reactive, ref, watch } from "vue";
import useAvailableDates from "../composables/useAvailableDates";
import useCreateRequest from "../composables/useCreateRequest";
import { AvailableDates } from "../models/AvailableDates";
import { DepartureRequest, DepartureResponse } from "../models/QuickformApi";
import { Passenger, QuickformState } from "../models/QuickformStates";
import Passengers from "./passengers/PassengersForm.vue";
import VehicleForm from "./vehicle/VehicleForm.vue";

interface Props {
    hasReturn?: boolean;
    booking?: QuickformState;
}

const props = defineProps<Props>();

const { getDates } = useAvailableDates();

const passengerStore = usePassengerStore();

const vehicleStore = useVehicleStore();

const quickFormStore = useQuickFormStore();

const outwardDates = ref<AvailableDates>();

const returnDates = ref<AvailableDates>();

const returnRoutes = ref<Filter[]>([]);

const showDates = ref(false);

const dropdownDate = ref();

const initialPage = (outward?: boolean) => {
    const date = outward ? state.outwardDate : state.returnDate ? state.returnDate : state.outwardDate;

    return dateObject(date ? new Date(date) : new Date());
};

onClickOutside(dropdownDate, () => (showDates.value = false));

const getReturnRoute = () => {
    if (props.booking?.outwardRoute) {
        returnRoutes.value = getReturnRoutes(props.booking.outwardRoute);
        getDates(props.booking.outwardRoute).then((dates) => {
            outwardDates.value = dates;
        });
        getDates(props.booking.returnRoute).then((dates) => {
            returnDates.value = dates;
        });
    }
    return props.booking?.returnRoute ?? "";
};

const state = reactive<QuickformState>({
    passengers: {
        passengers: passengerStore.getPassengers(Direction.Outward),
        returnPassengers: passengerStore.getPassengers(Direction.Return),
    } as Passenger,
    outwardRoute: props.booking?.outwardRoute ?? "",
    returnRoute: getReturnRoute(),
    outwardDate: props.booking?.outwardDate ?? undefined,
    returnDate: props.booking?.returnDate ?? undefined,
});

const rules = {
    passengers: {
        required: helpers.withMessage("Need to have at least one adult on each journey", () => {
            return isValidPassengers(props.hasReturn);
        }),
    },
    outwardRoute: { required },
    returnRoute: {
        required: requiredIf(function () {
            return props.hasReturn;
        }),
    },
    outwardDate: { required },
    returnDate: {
        required: requiredIf(function () {
            return props.hasReturn;
        }),
    },
};

const v$ = useVuelidate(rules, state);

const datesPlaceholder = computed(() => getDatesPlaceholder(state.outwardDate, state.returnDate, props.hasReturn));

watch(
    () => state.outwardRoute,
    (current) => {
        if (current) {
            setReturnOptions(current);
        }
    },
);

const setReturnOptions = (outwardRoute: string, isSwitch?: boolean) => {
    returnRoutes.value = getReturnRoutes(outwardRoute);
    getDates(outwardRoute).then((dates) => {
        outwardDates.value = dates;
    });
    state.outwardDate = undefined;
    if (!isSwitch) {
        const defaultReturn = getDefaultReturn(outwardRoute);
        state.returnRoute = defaultReturn ? defaultReturn : returnRoutes.value[0].value;
        onReturnRouteSelect(state.returnRoute);
    }
};

const onReturnRouteSelect = (returnRoute: string) => {
    state.returnDate = undefined;
    getDates(returnRoute).then((dates) => {
        returnDates.value = dates;
    });
};

const switchRoutes = () => {
    if (!state.returnRoute) return;
    const outward = state.outwardRoute;
    state.outwardRoute = state.returnRoute;
    setReturnOptions(state.outwardRoute, true);
    state.returnRoute = outward;
    onReturnRouteSelect(state.returnRoute);
};

watch(
    () => state.outwardDate,
    (current) => {
        if (!current) return;
        if (!state.returnDate || current > state.returnDate) {
            state.returnDate = getNextValidDate(current, returnDates.value?.disabledDates);
        }
    },
);

const { create, isLoading } = useCreateRequest();

const { displayDialog } = useDialog();

const search = async () => {
    const isValid = await v$.value.$validate();

    if (!isValid) return;

    const vehiclesOutward = vehicleStore.getVehiclesOfRoute(Direction.Outward);
    const vehiclesReturn = vehicleStore.getVehiclesOfRoute(Direction.Return);

    const passengersOutward = passengerStore.getPassengersForApi(Direction.Outward);
    const passengersReturn = passengerStore.getPassengersForApi(Direction.Return);

    const request: DepartureRequest[] = [];

    request.push(createRequest(state, passengersOutward, vehiclesOutward));

    if (props.hasReturn) {
        request.push(
            createRequest(
                state,
                passengerStore.getDifferentDetails ? passengersReturn : passengersOutward,
                vehicleStore.getDifferentDetails ? vehiclesReturn : vehiclesOutward,
                props.hasReturn,
            ),
        );
    }

    create(request)
        .then((data) => {
            quickFormStore.setDepartures(request);
            window.location.href = "/book/departures";
        })
        .catch((err: AxiosError) => {
            const errResponse: DepartureResponse = err.response?.data as DepartureResponse;
            console.error("Error creating departure request", JSON.stringify(errResponse));
            if (errResponse.error) {
                //bokkit error code : 12033 length on vehicle too short
                displayDialog({
                    title: errResponse.message ?? "Something went wrong",
                    message:
                        errResponse.bookitErrorCode == 12009
                            ? "Sorry - you cannot book more vehicles than the number of travellers."
                            : "Something unexpected happened, please try again",
                    confirmText: "I understand",
                    type: DialogType.danger,
                });
            } else {
                quickFormStore.setDepartures(request);
                window.location.href = "/book/departures";
            }
        });
};
</script>

<style lang="scss" scoped>
.v-date-picker {
    padding: 15px;
}
.round-trip {
    display: flex;
    gap: 5px;

    @include desktop {
        flex-direction: column;
        align-items: center;
        gap: 20px;
    }

    .inputs {
        width: 100%;
        @include flex(row, center, center);

        @include desktop {
            flex-direction: column;
            gap: 25px;
        }

        &__dropdown {
            width: 100%;
            min-width: 150px;
            position: relative;

            --vs-dropdown-min-width: 200px;

            &:first-child {
                @include desktop-and-up {
                    :deep(.dropdown-btn) {
                        border-top-left-radius: 10px;
                        border-bottom-left-radius: 10px;
                        border-top-right-radius: 0px;
                        border-bottom-right-radius: 0px;
                    }
                }
            }

            &:last-child {
                @include desktop-and-up {
                    :deep(.dropdown-btn) {
                        border-top-left-radius: 0px;
                        border-bottom-left-radius: 0px;
                        border-top-right-radius: 10px;
                        border-bottom-right-radius: 10px;
                    }
                }
            }

            :deep(.vs__dropdown-toggle) {
                border-radius: 0px;
                --v-input-br: 0px;
                padding-top: 12px;
                padding-bottom: 12px;

                @include desktop {
                    border-radius: 10px;
                }
            }

            :deep(.dropdown-btn) {
                border-radius: 0px;
                --v-btn-br: 0px;

                @include desktop {
                    border-radius: 10px;
                }
            }

            label {
                font-size: 0.875rem;
                font-weight: bold;
                text-transform: uppercase;
            }
        }

        .outward-route,
        .return-route {
            :deep(.vs__dropdown-toggle) {
                padding-top: 13px;
                padding-bottom: 15px;
            }

            :deep(.caret) {
                height: 0.8rem;
            }
        }

        @include desktop-and-up {
            .outward-route-return {
                :deep(.vs__dropdown-toggle) {
                    padding-right: 10px;
                }
            }

            .return-container {
                position: relative;

                .switch-icon {
                    cursor: pointer;
                    background: url("../../../assets/icons/switch.svg");
                    background-repeat: no-repeat;
                    background-position: center;
                    width: 10px;
                    height: 10px;
                    position: absolute;
                    border-radius: 50%;
                    border: 1px solid var(--c-light-grey);
                    padding: 10px;
                    background-color: var(--c-base);
                    left: 0;
                    bottom: 0;
                    z-index: 2;
                    transform: translate3d(-50%, -11px, 0);
                }

                .return-route {
                    :deep(.vs__dropdown-toggle) {
                        padding-left: 15px;
                    }
                }
            }
        }
    }

    .button-submit {
        margin: 5px;
        display: flex;
        align-items: flex-end;

        @include desktop {
            max-width: 300px;
            width: 100%;
        }

        .v-btn {
            --v-btn-padding: 10px 50px;
            border-radius: 34px;
            min-width: 150px;
            padding: 12px 20px;
            font-size: 0.875rem;

            @include desktop {
                width: 100%;
            }
        }
    }
}
</style>
